import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiEdit2, FiX } from 'react-icons/fi';
import {
  Fund,
  FUND_LOGO_COLORS,
  Pipeline,
  STAGE_COLORS,
  UpdateFundInput,
} from '../../types/fundraise';
import { formatAmount } from '../../utils/helpers';

const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = FiEdit2 as React.FC<React.SVGProps<SVGSVGElement>>;

interface FundDetailModalProps {
  fund: Fund;
  pipeline: Pipeline;
  onClose: () => void;
  onSave: (id: string, input: UpdateFundInput) => Promise<void>;
  readOnly?: boolean;
}

const stageNameFor = (pipeline: Pipeline, stageId: string): string =>
  pipeline.stages.find((s) => s.id === stageId)?.name ?? stageId;

const amountRange = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return '—';
  if (min === null) return formatAmount(max);
  if (max === null) return formatAmount(min);
  return `${formatAmount(min)} – ${formatAmount(max)}`;
};

const FundDetailModal: React.FC<FundDetailModalProps> = ({
  fund,
  pipeline,
  onClose,
  onSave,
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  // Edit-mode form state, initialised from the fund when entering edit mode.
  const [name, setName] = useState(fund.name);
  const [minAmount, setMinAmount] = useState(
    fund.min_amount === null ? '' : String(fund.min_amount)
  );
  const [maxAmount, setMaxAmount] = useState(
    fund.max_amount === null ? '' : String(fund.max_amount)
  );
  const [stage, setStage] = useState(fund.stage);
  const [logoInitials, setLogoInitials] = useState(fund.logo_initials ?? '');
  const [logoColor, setLogoColor] = useState(fund.logo_color ?? 'purple');
  const [websiteUrl, setWebsiteUrl] = useState(fund.website_url ?? '');
  const [contactName, setContactName] = useState(fund.contact_name ?? '');
  const [contactEmail, setContactEmail] = useState(fund.contact_email ?? '');
  const [notes, setNotes] = useState(fund.notes ?? '');

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetEditState = useCallback(() => {
    setName(fund.name);
    setMinAmount(fund.min_amount === null ? '' : String(fund.min_amount));
    setMaxAmount(fund.max_amount === null ? '' : String(fund.max_amount));
    setStage(fund.stage);
    setLogoInitials(fund.logo_initials ?? '');
    setLogoColor(fund.logo_color ?? 'purple');
    setWebsiteUrl(fund.website_url ?? '');
    setContactName(fund.contact_name ?? '');
    setContactEmail(fund.contact_email ?? '');
    setNotes(fund.notes ?? '');
    setError(null);
  }, [fund]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    },
    [isSaving, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) onClose();
  };

  const handleStartEdit = () => {
    resetEditState();
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    resetEditState();
    setIsEditing(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Fund name is required');
      return;
    }
    if (!stage) {
      setError('Please select a stage');
      return;
    }

    const minAmountNum = minAmount ? parseInt(minAmount, 10) : null;
    const maxAmountNum = maxAmount ? parseInt(maxAmount, 10) : null;
    if (minAmount && (minAmountNum === null || Number.isNaN(minAmountNum))) {
      setError('Min amount must be a valid number');
      return;
    }
    if (maxAmount && (maxAmountNum === null || Number.isNaN(maxAmountNum))) {
      setError('Max amount must be a valid number');
      return;
    }

    setIsSaving(true);
    try {
      const input: UpdateFundInput = {
        name: name.trim(),
        min_amount: minAmountNum,
        max_amount: maxAmountNum,
        stage,
        logo_initials: logoInitials || undefined,
        logo_color: logoColor,
        website_url: websiteUrl || undefined,
        contact_name: contactName || undefined,
        contact_email: contactEmail || undefined,
        notes: notes || undefined,
      };
      await onSave(fund.id, input);
      setIsEditing(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const logoSwatch = STAGE_COLORS[fund.logo_color] || '#8b5cf6';

  const viewBody = (
    <div className="su-fundraise-modal-body">
      <div className="su-fundraise-detail-summary">
        <div
          className="su-fundraise-detail-logo"
          style={{ backgroundColor: logoSwatch }}
        >
          {(fund.logo_initials || fund.name.slice(0, 2)).toUpperCase()}
        </div>
        <div>
          <div className="su-fundraise-detail-name">{fund.name}</div>
          <div className="su-fundraise-detail-stage">
            {stageNameFor(pipeline, fund.stage)}
          </div>
        </div>
      </div>

      <div className="su-fundraise-form-row">
        <div>
          <div className="su-fundraise-label">Min Amount</div>
          <div className="su-fundraise-detail-value">
            {fund.min_amount === null ? '—' : formatAmount(fund.min_amount)}
          </div>
        </div>
        <div>
          <div className="su-fundraise-label">Max Amount</div>
          <div className="su-fundraise-detail-value">
            {fund.max_amount === null ? '—' : formatAmount(fund.max_amount)}
          </div>
        </div>
      </div>

      <div className="su-fundraise-form-row">
        <div>
          <div className="su-fundraise-label">Check Range</div>
          <div className="su-fundraise-detail-value">
            {amountRange(fund.min_amount, fund.max_amount)}
          </div>
        </div>
        <div>
          <div className="su-fundraise-label">Stage</div>
          <div className="su-fundraise-detail-value">
            {stageNameFor(pipeline, fund.stage)}
          </div>
        </div>
      </div>

      <div className="su-fundraise-form-group">
        <div className="su-fundraise-label">Website</div>
        {fund.website_url ? (
          <a
            href={fund.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="su-fundraise-detail-link"
          >
            {fund.website_url}
          </a>
        ) : (
          <div className="su-fundraise-detail-value">—</div>
        )}
      </div>

      <div className="su-fundraise-form-row">
        <div>
          <div className="su-fundraise-label">Contact Name</div>
          <div className="su-fundraise-detail-value">{fund.contact_name || '—'}</div>
        </div>
        <div>
          <div className="su-fundraise-label">Contact Email</div>
          {fund.contact_email ? (
            <a
              href={`mailto:${fund.contact_email}`}
              className="su-fundraise-detail-link"
            >
              {fund.contact_email}
            </a>
          ) : (
            <div className="su-fundraise-detail-value">—</div>
          )}
        </div>
      </div>

      <div className="su-fundraise-form-group">
        <div className="su-fundraise-label">Notes</div>
        <div className="su-fundraise-detail-value su-fundraise-detail-notes">
          {fund.notes || '—'}
        </div>
      </div>
    </div>
  );

  const editBody = (
    <div className="su-fundraise-modal-body">
      {error && <div className="su-fundraise-error-message">{error}</div>}

      <div className="su-fundraise-form-group">
        <label htmlFor="fund-edit-name" className="su-fundraise-label">
          Fund Name *
        </label>
        <input
          id="fund-edit-name"
          type="text"
          className="su-fundraise-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      <div className="su-fundraise-form-row">
        <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="fund-edit-min" className="su-fundraise-label">
            Min Amount ($)
          </label>
          <input
            id="fund-edit-min"
            type="number"
            min="0"
            className="su-fundraise-input"
            value={minAmount}
            onChange={(e) => setMinAmount(e.target.value)}
          />
        </div>
        <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="fund-edit-max" className="su-fundraise-label">
            Max Amount ($)
          </label>
          <input
            id="fund-edit-max"
            type="number"
            min="0"
            className="su-fundraise-input"
            value={maxAmount}
            onChange={(e) => setMaxAmount(e.target.value)}
          />
        </div>
      </div>

      <div className="su-fundraise-form-group">
        <label htmlFor="fund-edit-stage" className="su-fundraise-label">
          Stage *
        </label>
        <select
          id="fund-edit-stage"
          className="su-fundraise-input"
          value={stage}
          onChange={(e) => setStage(e.target.value)}
        >
          {pipeline.stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="su-fundraise-form-row">
        <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="fund-edit-initials" className="su-fundraise-label">
            Logo Initials
          </label>
          <input
            id="fund-edit-initials"
            type="text"
            className="su-fundraise-input"
            maxLength={2}
            value={logoInitials}
            onChange={(e) => setLogoInitials(e.target.value.toUpperCase())}
          />
        </div>
        <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
          <div className="su-fundraise-label">Logo Color</div>
          <div className="su-color-picker">
            {FUND_LOGO_COLORS.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`su-color-option ${logoColor === c.id ? 'selected' : ''}`}
                style={{ backgroundColor: c.bg }}
                onClick={() => setLogoColor(c.id)}
                aria-label={`Select ${c.id} color`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="su-fundraise-form-group">
        <label htmlFor="fund-edit-website" className="su-fundraise-label">
          Website URL
        </label>
        <input
          id="fund-edit-website"
          type="url"
          className="su-fundraise-input"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
        />
      </div>

      <div className="su-fundraise-form-row">
        <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="fund-edit-contact-name" className="su-fundraise-label">
            Contact Name
          </label>
          <input
            id="fund-edit-contact-name"
            type="text"
            className="su-fundraise-input"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
          />
        </div>
        <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
          <label htmlFor="fund-edit-contact-email" className="su-fundraise-label">
            Contact Email
          </label>
          <input
            id="fund-edit-contact-email"
            type="email"
            className="su-fundraise-input"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
          />
        </div>
      </div>

      <div className="su-fundraise-form-group">
        <label htmlFor="fund-edit-notes" className="su-fundraise-label">
          Notes
        </label>
        <textarea
          id="fund-edit-notes"
          className="su-fundraise-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
    </div>
  );

  const modal = (
    <div className="su-fundraise-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="su-fundraise-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fund-detail-title"
      >
        <div className="su-fundraise-modal-header">
          <div>
            <h2 id="fund-detail-title" className="su-fundraise-modal-title">
              {isEditing ? 'Edit Fund' : 'Fund Details'}
            </h2>
            <p className="su-fundraise-modal-subtitle">
              {isEditing ? 'Update fund information' : pipeline.name}
            </p>
          </div>
          <button
            type="button"
            className="su-fundraise-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        {isEditing ? (
          <form onSubmit={handleSave}>
            {editBody}
            <div className="su-fundraise-modal-footer">
              <button
                type="button"
                className="su-btn-secondary"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="su-btn-primary"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <>
            {viewBody}
            <div className="su-fundraise-modal-footer">
              <button
                type="button"
                className="su-btn-secondary"
                onClick={onClose}
              >
                Close
              </button>
              {!readOnly && (
                <button
                  type="button"
                  className="su-btn-primary"
                  onClick={handleStartEdit}
                >
                  <EditIcon width={14} height={14} />
                  <span style={{ marginLeft: 6 }}>Edit</span>
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default FundDetailModal;
